import { PillarKey, PreofferRouteTally, SimulationResult, SimulationTier } from './risk-scorecard-config.interface'
import { BAND_UI } from '@shared/components/ciec-analysis-view/tabs/risk-scorecard.labels'
import { PillTone } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { RiskBand } from '@data/interfaces/risk-scorecard.interface'

/**
 * Lo que pinta el simulador (plan v4.2 §10, E4), ya calculado a partir de la respuesta de la API: tablas de bandas,
 * motivos y tramos con la activa y la candidata lado a lado. Funciones puras para probarlas sin la plantilla.
 */

//* Los mismos límites que la API (`MAX_SIMULATION_SAMPLE`): 300 clientes caben en el tiempo de una petición.
export const SIMULATION_SAMPLE = { default: 100, min: 1, max: 300 } as const

const BANDS: RiskBand[] = ['favorable', 'review', 'unfavorable']
export const SIMULATION_TIERS: SimulationTier[] = [20, 15, 10, 0]

const SKIPPED_LABELS: Readonly<Record<string, string>> = {
  no_verification: 'sin análisis fiscal',
  pre_snapshot: 'con un análisis anterior a la foto de Syntage',
}

export interface BandRow {
  band: RiskBand
  label: string
  tone: PillTone
  baseline: number
  candidate: number
  baselinePct: number | null
  candidatePct: number | null
  delta: number
}

export interface ReasonRow {
  code: string
  label: string
  kind: 'veto' | 'coverage'
  baseline: number
  candidate: number
}

export interface MetricRow {
  key: string
  label: string
  pillar: PillarKey
  tiers: Record<SimulationTier, number>
  noData: number
  //* En cuántas empresas la candidata da otro tramo que la activa.
  changed: number
}

export function pct(count: number, total: number): number | null {
  return total > 0 ? (count / total) * 100 : null
}

export function bandRows(result: SimulationResult): BandRow[] {
  const total = result.sample.companies
  return BANDS.map((band) => {
    const baseline = result.baseline.bands[band] ?? 0
    const candidate = result.candidate.bands[band] ?? 0
    return { band, label: BAND_UI[band].label, tone: BAND_UI[band].tone, baseline, candidate, baselinePct: pct(baseline, total), candidatePct: pct(candidate, total), delta: candidate - baseline }
  })
}

/** Los cruces donde la banda cambia, de más a menos casos. Los que se quedan igual van en `unchangedCount`. */
export function movedTransitions(result: SimulationResult): Array<{ from: string; to: string; fromTone: PillTone; toTone: PillTone; count: number }> {
  return result.transitions
    .filter((item) => item.from !== item.to)
    .sort((a, b) => b.count - a.count)
    .map((item) => ({ from: BAND_UI[item.from].label, to: BAND_UI[item.to].label, fromTone: BAND_UI[item.from].tone, toTone: BAND_UI[item.to].tone, count: item.count }))
}

export function unchangedCount(result: SimulationResult): number {
  return result.transitions.filter((item) => item.from === item.to).reduce((acc, item) => acc + item.count, 0)
}

export function reasonRows(result: SimulationResult): ReasonRow[] {
  const rows = new Map<string, ReasonRow>()
  const add = (side: 'baseline' | 'candidate') => {
    for (const reason of result[side].reasons) {
      const row = rows.get(reason.code) ?? { code: reason.code, label: reason.label, kind: reason.kind, baseline: 0, candidate: 0 }
      row[side] = reason.count
      rows.set(reason.code, row)
    }
  }
  add('baseline')
  add('candidate')
  return [...rows.values()].sort((a, b) => Math.max(b.baseline, b.candidate) - Math.max(a.baseline, a.candidate) || a.label.localeCompare(b.label))
}

/** Los tramos de la candidata por métrica, en el orden del motor. */
export function metricRows(result: SimulationResult): MetricRow[] {
  return result.candidate.metrics.map((metric) => ({
    key: metric.key,
    label: metric.label,
    pillar: metric.pillar,
    tiers: metric.tiers,
    noData: metric.states.sin_dato ?? 0,
    changed: result.metricChanges[metric.key] ?? 0,
  }))
}

/** Lo que quedó fuera de la comparación, en frases cortas. Vacío si entraron todos. */
export function sampleNotes(result: SimulationResult): string[] {
  const { individuals, skipped, failed, demoExcluded } = result.sample
  const [one, many] = ['cliente', 'clientes']
  const notes: string[] = []
  if (individuals) notes.push(`${individuals} ${individuals === 1 ? 'persona física' : 'personas físicas'}: la calculadora no les da banda`)
  for (const [state, count] of Object.entries(skipped)) if (count) notes.push(`${count} ${count === 1 ? one : many} ${SKIPPED_LABELS[state] ?? `en estado ${state}`}`)
  if (failed) notes.push(`${failed} con error al calcular`)
  if (demoExcluded) notes.push(`${demoExcluded} ${demoExcluded === 1 ? 'foto' : 'fotos'} de demostración fuera de la muestra`)
  return notes
}

// ── Preofertas (plan v4.3 §9.4) ───────────────────────────────────────────────

export interface PreofferRouteRow {
  key: keyof Omit<PreofferRouteTally, 'reasons'>
  label: string
  tone: PillTone
  baseline: number
  candidate: number
  delta: number
  //* Si el cambio es bueno o malo: más emitidas es bueno, más en revisión es malo; Legal y sin calcular no se juzgan.
  trend: 'up' | 'down' | null
}

const PREOFFER_ROUTES: ReadonlyArray<{ key: PreofferRouteRow['key']; label: string; tone: PillTone }> = [
  { key: 'issued', label: 'Habrían salido', tone: 'ok' },
  { key: 'legalReview', label: 'Con Legal', tone: 'info' },
  { key: 'notEligible', label: 'En revisión', tone: 'warn' },
  { key: 'uncomputable', label: 'Sin calcular', tone: 'neutral' },
]

/** A dónde irían las preofertas con la activa y con la candidata. Vacío si la simulación no fue sobre clientes nuevos. */
export function preofferRouteRows(result: SimulationResult): PreofferRouteRow[] {
  const preoffers = result.preoffers
  if (!preoffers) return []
  return PREOFFER_ROUTES.map(({ key, label, tone }): PreofferRouteRow => {
    const baseline = preoffers.baseline[key] ?? 0
    const candidate = preoffers.candidate[key] ?? 0
    const delta = candidate - baseline
    const sign = key === 'issued' ? Math.sign(delta) : key === 'notEligible' ? -Math.sign(delta) : 0
    return { key, label, tone, baseline, candidate, delta, trend: sign > 0 ? 'up' : sign < 0 ? 'down' : null }
  }).filter((row) => row.key !== 'uncomputable' || row.baseline || row.candidate)
}

/** Por qué quedarían en revisión, con la activa y la candidata lado a lado. */
export function preofferReasonRows(result: SimulationResult): Array<{ code: string; label: string; baseline: number; candidate: number }> {
  const preoffers = result.preoffers
  if (!preoffers) return []
  const rows = new Map<string, { code: string; label: string; baseline: number; candidate: number }>()
  for (const side of ['baseline', 'candidate'] as const) {
    for (const reason of preoffers[side].reasons) {
      const row = rows.get(reason.code) ?? { code: reason.code, label: reason.label, baseline: 0, candidate: 0 }
      row[side] = reason.count
      rows.set(reason.code, row)
    }
  }
  return [...rows.values()].sort((a, b) => Math.max(b.baseline, b.candidate) - Math.max(a.baseline, a.candidate) || a.label.localeCompare(b.label))
}
